import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';

import AptitudeAnalytics from '../components/aptitude/AptitudeAnalytics';
import './StudentProfilePro.css';

const initials = (n) => n ? n.split(' ').map(x => x[0]).join('').toUpperCase().slice(0, 2) : 'ST';
const statusStyle = (s) => ({
  'placed': { bg: '#e5f4ef', color: '#059669' },
  'not placed': { bg: '#fdeaea', color: '#dc2626' },
  'in process': { bg: '#fbf0e0', color: '#d97706' }
}[s] || { bg: '#f1f5f9', color: '#64748b' });

// Admin → View Student → Aptitude Analytics.
// Continuous-performance module (aptitude_profile + aptitude_attempt_scores),
// intentionally styled and structured differently from the one-time Diagnostic
// report. All analytics are read from stored metrics — never recalculated.
const StudentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [aptProfile, setAptProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      // Aptitude-only continuous profile (no diagnostic data on this page).
      const res = await API.get(`/reports/student/${id}/aptitude`);
      setProfile(res.data.student_profile);
      setAptProfile(res.data.aptitude_profile || null);
      setHistory(res.data.history || []);
      setTrend(res.data.trend || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student profile.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="diagnostic-page-bg"><div className="apt-loading">Loading continuous performance profile…</div></div>;
  if (error || !profile) return (
    <div className="diagnostic-page-bg" style={{ padding: 40 }}>
      <button className="apt-back" onClick={() => navigate('/admin/students')}><Icon name="chevron-left" size={16} /> Back</button>
      <div className="apt-empty"><span className="i"><Icon name="warning" size={36} /></span><h3>Profile Unavailable</h3><p>{error || 'Student not found.'}</p></div>
    </div>
  );

  const st = statusStyle(profile.placement_status);

  return (
    <div className="diagnostic-page-bg">
      <div className="diagnostic-dashboard-wrapper">
        <button className="apt-back" onClick={() => navigate('/admin/students')}><Icon name="chevron-left" size={16} /> Back to Students</button>

        {/* Personal + academic header (admin context) */}
        <div className="spx-header">
          <div className="spx-avatar">{initials(profile.name)}</div>
          <div className="spx-hinfo">
            <h1>{profile.name}</h1>
            <div className="spx-meta">{profile.roll_number || '—'} · {profile.branch || '—'}{profile.year_of_study ? ` · Year ${profile.year_of_study}` : ''} · Batch {profile.placement_batch_year || '—'}</div>
            <div className="spx-meta">{profile.email} · {profile.phone || 'No phone'}</div>
            <span className="spx-status" style={{ background: st.bg, color: st.color }}>{profile.placement_status || 'unknown'}</span>
          </div>
          <div className="spx-hstats">
            <div className="spx-hstat"><div className="v">{Number(profile.cgpa) > 0 ? Number(profile.cgpa).toFixed(2) : '—'}</div><div className="l">CGPA</div></div>
            <div className="spx-hstat"><div className="v" style={{ color: Number(profile.arrears) > 0 ? '#dc2626' : '#059669' }}>{profile.arrears ?? 0}</div><div className="l">Arrears</div></div>
            <div className="spx-hstat"><div className="v">{history.length}</div><div className="l">Aptitude Tests</div></div>
          </div>
        </div>

        <AptitudeAnalytics
          profile={aptProfile}
          history={history}
          trend={trend}
          subtitle={`Continuous aptitude performance for ${profile.name?.split(' ')[0] || 'this student'} — every attempt, tracked over time.`}
        />
      </div>
    </div>
  );
};

export default StudentProfile;
